export type UnknownFunction = (...args: unknown[]) => unknown

export function promiseLimiter(concurrency: number = 1): (fn: UnknownFunction, ...args: unknown[]) => Promise<unknown> {
  if (concurrency <= 0) {
    throw new TypeError('concurrency must be greater than 0')
  }

  const queue: (() => void)[] = []
  let activeCount: number = 0

  const next = (): void => {
    activeCount--
    if (queue.length > 0) {
      queue.shift()!()
    }
  }

  const run = (fn: UnknownFunction, resolve: UnknownFunction, ...args: unknown[]): void => {
    activeCount++
    const result = fn(args)
    resolve(result)
    Promise.resolve(result).then(next, next)
  }

  const enqueue = (fn: UnknownFunction, resolve: UnknownFunction, ...args: unknown[]): undefined | number => {
    if (activeCount < concurrency) {
      run(fn, resolve, ...args)
      return
    }
    return queue.push(run.bind(null, fn, resolve, ...args))
  }

  const generator = async (fn: UnknownFunction, ...args: unknown[]): Promise<unknown> =>
    await new Promise((resolve) => enqueue(fn, resolve, ...args))

  return generator
}

export const PromiseWithResolvers = <T = void>() => {
  let resolve: (value: T | PromiseLike<T>) => void
  let reject: (reason?: unknown) => void

  const promise = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })

  return {
    promise,
    resolve: resolve!,
    reject: reject!,
  }
}
